import { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

// material-ui
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

// ant design icons
import CheckCircleFilled from '@ant-design/icons/CheckCircleFilled';
import CloudUploadOutlined from '@ant-design/icons/CloudUploadOutlined';
import FilePdfOutlined from '@ant-design/icons/FilePdfOutlined';
import FileImageOutlined from '@ant-design/icons/FileImageOutlined';

// project imports
import { importIepPdf } from 'services/api/iep';
import { formatApiError } from 'utils/normalizeDate';

// ==============================|| IEP - IMPORT FROM AN ASSESSMENT REPORT ||============================== //

const MAX_MB = 20;

/**
 * Upload another centre's ABA assessment report; the IEP form fills in behind it.
 *
 * WHY THIS IS NOT ImportIntakeDialog. That dialog reports a COUNT - "172 answers are
 * ready to fill in" - because the intake is a flat list of questions and a count is a
 * fair summary of it. An IEP is not: an import that brings back 19 goals, 4
 * behaviours, a strategy table and eight narrative sections is not usefully described
 * as "43 answers", and the one number hides that the goals arrived without dates and
 * the assessment rows are still empty. So this one names what came back, section by
 * section, and is equally specific about what did not.
 *
 * WHAT IT WILL NOT DO is stated before you commit. The IEP's own dates, the version
 * label, the child's identity and both signatures are never taken from a document -
 * they are decisions, not content - and the assessment's per-domain scores exist only
 * as coloured squares in a grid. Those are listed as things to finish, and left blank
 * rather than guessed, because a blank prompts someone to look and a guess does not.
 *
 * Nothing is saved here. Filling the form is not submitting it; the form's own Save
 * is still what writes anything down.
 */
export default function ImportIepDialog({ open, onClose, onApply }) {
  const theme = useTheme();
  const intl = useIntl();
  const t = useCallback((id, values) => intl.formatMessage({ id }, values), [intl]);

  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [dragging, setDragging] = useState(false);

  const reset = () => {
    setFile(null);
    setBusy(false);
    setError(null);
    setResult(null);
    setDragging(false);
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const accept = (picked) => {
    if (!picked) return;
    setError(null);
    if (picked.type && picked.type !== 'application/pdf') {
      setError(t('iepImport.error.notPdf'));
      return;
    }
    if (picked.size > MAX_MB * 1024 * 1024) {
      setError(t('iepImport.error.tooBig', { mb: MAX_MB }));
      return;
    }
    setFile(picked);
    setResult(null);
  };

  const read = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      setResult(await importIepPdf(file));
    } catch (err) {
      // See ImportIntakeDialog: utils/axios rejects with the response body, so the
      // `err.response.data` read never matched and every failure looked like a bad PDF.
      setError(formatApiError(err, t('iepImport.error.generic')));
    } finally {
      setBusy(false);
    }
  };

  /**
   * What came back, in the form's own words rather than its field names.
   *
   * Driven off the patch the server returned, so a section the document had nothing
   * for is simply absent instead of showing "0" - a row of zeroes reads as a failed
   * import when it is usually a report that does not have that section.
   */
  const summarise = (patch = {}) => {
    const counts = [
      ['iepGoals', 'iepImport.summary.goals'],
      ['parentGoals', 'iepImport.summary.parentGoals'],
      ['behaviors', 'iepImport.summary.behaviors'],
      ['pbspStrategies', 'iepImport.summary.strategies'],
      ['assessments', 'iepImport.summary.assessments']
    ]
      .filter(([key]) => Array.isArray(patch[key]) && patch[key].length > 0)
      .map(([key, id]) => t(id, { count: patch[key].length }));

    // Everything that is not one of the lists above is a narrative or identification
    // field, and naming eight of them individually is noise - the count is the useful
    // shape here, and the form is where they get read.
    const listKeys = new Set(['iepGoals', 'parentGoals', 'behaviors', 'pbspStrategies', 'assessments']);
    const sections = Object.keys(patch).filter((key) => !listKeys.has(key)).length;
    if (sections > 0) counts.unshift(t('iepImport.summary.sections', { count: sections }));
    return counts;
  };

  const summary = result ? summarise(result.patch) : [];
  const attention = result?.needs_attention || [];
  const images = result?.images || [];
  const hasPatch = Boolean(result && Object.keys(result.patch || {}).length > 0);

  const apply = () => {
    onApply(result.patch || {}, {
      fileName: file?.name,
      needsAttention: attention,
      // Which boxes the report could not fill, so the form can mark them.
      unfilled: result.unfilled || [],
      images,
      summary
    });
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <FilePdfOutlined />
          <Box>
            <Typography variant="h4">{t('iepImport.title')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('iepImport.subtitle')}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {String(error)}
          </Alert>
        )}

        {/* --- step 1: choose the file --- */}
        {!result && (
          <>
            <Box
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                accept(e.dataTransfer.files?.[0]);
              }}
              onClick={() => !busy && inputRef.current?.click()}
              sx={{
                p: 5,
                textAlign: 'center',
                cursor: busy ? 'default' : 'pointer',
                borderRadius: 2,
                border: `2px dashed ${dragging ? theme.palette.primary.main : theme.palette.divider}`,
                bgcolor: dragging ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
                transition: theme.transitions.create(['border-color', 'background-color'])
              }}
            >
              <input ref={inputRef} type="file" accept="application/pdf,.pdf" hidden onChange={(e) => accept(e.target.files?.[0])} />
              <CloudUploadOutlined style={{ fontSize: 34, color: theme.palette.text.secondary }} />
              <Typography variant="h5" sx={{ mt: 1.5 }}>
                {file ? file.name : t('iepImport.drop')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {file
                  ? t('iepImport.fileSize', { kb: Math.round(file.size / 1024).toLocaleString(intl.locale) })
                  : t('iepImport.dropHint', { mb: MAX_MB })}
              </Typography>
            </Box>

            {busy && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress />
                <Typography variant="caption" color="text.secondary">
                  {t('iepImport.readingHint')}
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* --- step 2: say what came back, then fill --- */}
        {result && (
          <>
            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
              <CheckCircleFilled style={{ fontSize: 22, color: theme.palette.success.main, marginTop: 2 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h5">{t(hasPatch ? 'iepImport.ready' : 'iepImport.readyEmpty')}</Typography>
                {hasPatch && (
                  <>
                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1, mb: 1 }}>
                      {summary.map((line) => (
                        <Chip key={line} size="small" label={line} variant="outlined" />
                      ))}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {t('iepImport.readyHint')}
                    </Typography>
                  </>
                )}
              </Box>
            </Stack>

            {images.length > 0 && (
              <Alert severity="success" icon={<FileImageOutlined />} sx={{ mb: 1 }}>
                {t('iepImport.imagesFound', { count: images.length })}
              </Alert>
            )}

            {attention.length > 0 && (
              <Alert severity="info" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {t('iepImport.checkTitle', { count: attention.length })}
                </Typography>
                <List dense disablePadding sx={{ maxHeight: 230, overflowY: 'auto' }}>
                  {attention.map((item) => (
                    <ListItem key={`${item.key}-${item.label}`} disableGutters sx={{ py: 0.4, display: 'block' }}>
                      <ListItemText
                        primary={item.label}
                        secondary={item.reason}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}

            {(result.warnings || []).map((w) => (
              <Alert severity="warning" key={w} sx={{ mb: 1 }}>
                {w}
              </Alert>
            ))}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="secondary" disabled={busy}>
          {t('common.cancel')}
        </Button>
        {!result ? (
          <Button
            variant="contained"
            onClick={read}
            disabled={!file || busy}
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {busy ? t('iepImport.reading') : t('iepImport.read')}
          </Button>
        ) : (
          <Button variant="contained" onClick={apply} disabled={!hasPatch}>
            {t('iepImport.fillForm')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

ImportIepDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  /** (patch, { fileName, needsAttention, images, summary }) - merged into the form. */
  onApply: PropTypes.func.isRequired
};
